'use client';

import React, { createContext, useContext, useMemo, useEffect } from 'react';
import { useContentArtifacts } from '@/hooks/useContentArtifacts';
import type { ArtifactContent } from '@/components/chat/ContentArtifact';
import { useClient } from '@/context/ClientContext';
import { useAuth } from '@/context/AuthContext';
import { contentManagement } from '@/lib/contentManagement';
import { resolveArtifactCheckpointId } from '@/lib/contentArtifacts/approvalReadiness';

type ArtifactsCtx = ReturnType<typeof useContentArtifacts>;

const ArtifactsContext = createContext<ArtifactsCtx | null>(null);

interface ArtifactsProviderProps {
  children: React.ReactNode;
  autoLoadServer?: boolean;
}

export function ArtifactsProvider({ children, autoLoadServer = true }: ArtifactsProviderProps) {
  const artifactsState = useContentArtifacts();
  const { activeClient } = useClient();
  const { currentUser } = useAuth() as any;

  // Load server artifacts when active client/user is available
  useEffect(() => {
    if (!autoLoadServer) return;
    let cancelled = false;
    const load = async () => {
      try {
        const clientId = (activeClient as any)?.id;
        const userId = (currentUser as any)?.uid;
        if (!clientId || !userId) return;

        const docs = await contentManagement.getClientContent(clientId, userId);
        if (cancelled) return;

        const mapDocType = (t: any): ArtifactContent['type'] => {
          switch (t) {
            case 'press_release': return 'press_release';
            case 'social_post': return 'social_media';
            case 'email_template': return 'email';
            case 'statement': return 'statement';
            case 'template':
            case 'strategy':
            case 'boilerplate':
              return 'content';
            default:
              return 'content';
          }
        };

        const mapped: ArtifactContent[] = (docs || []).map((d: any) => {
          const runId = typeof d?.provenance?.supervisor?.runId === 'string'
            ? d.provenance.supervisor.runId
            : typeof d?.metadata?.runId === 'string'
              ? d.metadata.runId
              : undefined;
          const checkpointId = resolveArtifactCheckpointId({
            checkpointId: d?.checkpointId || d?.metadata?.checkpointId,
            runId,
            requestId: d?.requestId || d?.metadata?.requestId,
            metadata: d?.metadata,
            review: d?.review,
            data: d,
            pressRelease: d?.pressRelease || d?.pressReleaseV1 || d?.metadata?.pressReleaseV1 || d?.metadata?.pressRelease,
          });
          return {
            id: d.id,
            type: mapDocType(d.type),
            title: d.title || 'Artifact',
            content: d.content || '',
            agentType: 'ai',
            timestamp: new Date(d.updatedAt || d.createdAt || Date.now()),
            version: d.version || 1,
            history: [],
            lastUpdated: new Date(d.updatedAt || d.createdAt || Date.now()),
            isSavedToLibrary: true,
            savedToLibraryAt: new Date(d.updatedAt || d.createdAt || Date.now()),
            ...(runId ? { runId } : {}),
            ...(checkpointId ? { checkpointId } : {}),
            ...(typeof d?.metadata?.artifactKey === 'string'
              ? { artifactKey: d.metadata.artifactKey, lineageId: d.metadata.lineageId || d.metadata.artifactKey }
              : {}),
            ...(typeof d?.sourceConversationId === 'string' ? { sourceConversationId: d.sourceConversationId } : {}),
            ...(d.policy?.issues ? { policyIssues: d.policy.issues } : {}),
            ...(d.citationPackV1 ? { citationPackV1: d.citationPackV1 } : d.metadata?.citationPackV1 ? { citationPackV1: d.metadata.citationPackV1 } : {}),
            ...(Array.isArray(d.approvalEnvelopes) ? { approvalEnvelopes: d.approvalEnvelopes } : Array.isArray(d.metadata?.approvalEnvelopes) ? { approvalEnvelopes: d.metadata.approvalEnvelopes } : {}),
            ...(d.pressRelease ? { pressRelease: d.pressRelease } : d.pressReleaseV1 ? { pressRelease: d.pressReleaseV1 } : d.metadata?.pressReleaseV1 ? { pressRelease: d.metadata.pressReleaseV1 } : d.metadata?.pressRelease ? { pressRelease: d.metadata.pressRelease } : {}),
            ...(d.reviewerFeedback ? { reviewerFeedback: d.reviewerFeedback } : d.metadata?.reviewerFeedback ? { reviewerFeedback: d.metadata.reviewerFeedback } : d.review?.reviewerFeedback ? { reviewerFeedback: d.review.reviewerFeedback } : {}),
          };
        });

        artifactsState.setArtifacts((prev) => {
          const byId = new Map<string, ArtifactContent>();
          for (const a of prev) byId.set(a.id, a);
          for (const m of mapped) byId.set(m.id, { ...(byId.get(m.id) || {} as any), ...m });
          return Array.from(byId.values()).sort((a, b) => (b.lastUpdated?.getTime() || 0) - (a.lastUpdated?.getTime() || 0));
        });
      } catch (err) {
        console.error('Failed to load server artifacts (provider)', err);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [autoLoadServer, (activeClient as any)?.id, (currentUser as any)?.uid]);

  const value = useMemo(() => artifactsState, [artifactsState]);
  return (
    <ArtifactsContext.Provider value={value}>
      {children}
    </ArtifactsContext.Provider>
  );
}

// Hook for consumers that require the provider
export function useArtifactsContext() {
  const ctx = useContext(ArtifactsContext);
  if (!ctx) throw new Error('useArtifactsContext must be used within ArtifactsProvider');
  return ctx;
}

// Bridge hook: prefer provider when present, otherwise fall back to local hook.
export function useArtifactsBridge(): ArtifactsCtx {
  const ctx = useContext(ArtifactsContext);
  return ctx || useContentArtifacts();
}
